import React, { useMemo, useState } from 'react'
import { Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { AddIcon } from '@reapit/elements/icons/add'
import { toast } from '@reapit/elements/core/toaster'
import { AdminPdfField, AdminPdfTemplate, FormFieldLinkOption } from '../../../../api/types'
import { useAdminPdfTemplateFields, useAdminPdfTemplates } from '../../../../api/pdf-templates'
import { useAdminPdfFields } from '../../../../api/pdf-fields'
import { useBuilderDeletes } from '../../../../api/builder-deletes'
import { PdfFieldMappingDrawer } from '../pdf-field-mapping-drawer'
import { ConfirmDeleteDialog } from '../../../../components/confirm-delete-dialog'
import { useAccessMode } from '../../../../access-mode-provider'
import { EmptyState, SideCardHelp } from '../../style'
import {
  MappingActions,
  MappingCustomLabel,
  MappingFieldKey,
  MappingFieldRow,
  MappingFieldsPanel,
  MappingSectionHeading,
  MappingSourceTag,
  MappingSummary,
  MappingTabLayout,
  MappingTemplateRow,
} from './style'

interface PdfMappingTabProps {
  formReferenceID: string
  versionReferenceID: string
  formFieldOptions: FormFieldLinkOption[]
}

/** Plain-text fallback of what a mapping does, used for the row's title tooltip (React nodes can't go there). */
const describeMapping = (mapping: AdminPdfField | undefined, formFieldLabelByRef: Map<string, string>): string => {
  if (!mapping) return 'Not mapped'

  const parts: string[] = []
  if (mapping.formFieldReferenceID) {
    parts.push(`Linked to ${formFieldLabelByRef.get(mapping.formFieldReferenceID) ?? 'a form field'}`)
  }
  if (mapping.defaultData) {
    parts.push(`Default: ${mapping.defaultData}`)
  }
  if (mapping.conditionalValue) {
    const source = mapping.formFieldReferenceID ? "linked field's answer" : mapping.conditionalData || 'condition'
    parts.push(`if ${source} = "${mapping.conditionalValue}"`)
  }
  return parts.length > 0 ? parts.join(' · ') : 'Mapped'
}

/** Same description as describeMapping, but as tagged JSX — the linked field / merge token gets the same
 * pill treatment as the Recipient builder's link badges, instead of rendering as plain text. */
const renderMapping = (
  mapping: AdminPdfField | undefined,
  formFieldLabelByRef: Map<string, string>,
): React.ReactNode => {
  if (!mapping) return 'Not mapped'

  const parts: React.ReactNode[] = []
  if (mapping.formFieldReferenceID) {
    const fullLabel = formFieldLabelByRef.get(mapping.formFieldReferenceID) ?? 'a form field'
    const label = fullLabel.split(' · ').pop() ?? fullLabel
    parts.push(
      <span key="link">
        Linked to <MappingSourceTag>🔗 {label}</MappingSourceTag>
      </span>,
    )
  }
  if (mapping.defaultData) {
    parts.push(
      <span key="default">
        Default: <MappingSourceTag>{mapping.defaultData}</MappingSourceTag>
      </span>,
    )
  }
  if (mapping.conditionalValue) {
    const source = mapping.formFieldReferenceID ? "linked field's answer" : mapping.conditionalData || 'condition'
    parts.push(
      <span key="condition">
        if {source} = &quot;{mapping.conditionalValue}&quot;
      </span>,
    )
  }
  return parts.length > 0 ? parts : 'Mapped'
}

/**
 * The PDF Mapping tab: pick an uploaded PDF template, see every AcroForm field extracted from it, and map
 * each one to a form field or a default/merge-token value. Distinct from the PDF Templates tab, which only
 * handles upload/metadata — mappings live here.
 */
export const PdfMappingTab: React.FC<PdfMappingTabProps> = ({
  formReferenceID,
  versionReferenceID,
  formFieldOptions,
}) => {
  const { data: templates } = useAdminPdfTemplates(formReferenceID, versionReferenceID)
  const [selectedTemplate, setSelectedTemplate] = useState<AdminPdfTemplate | null>(null)
  const { data: extractedFields, loading: fieldsLoading } = useAdminPdfTemplateFields(
    formReferenceID,
    versionReferenceID,
    selectedTemplate?.referenceID,
  )
  const { data: mappings, refresh: refreshMappings } = useAdminPdfFields(formReferenceID, versionReferenceID)
  const { isReadOnly } = useAccessMode()

  const mappingByKey = useMemo(() => new Map(mappings.map((mapping) => [mapping.pdfFieldKey, mapping])), [mappings])
  const formFieldLabelByRef = useMemo(
    () => new Map(formFieldOptions.map((option) => [option.referenceID, option.label])),
    [formFieldOptions],
  )

  // Custom mappings — any saved FormPDFField whose key does not appear in the extracted PDF fields.
  // These are indexed/custom keys (e.g. Tenant{{idx}}_FullName) that were added via "Add Custom Map".
  // Only computed when a template is selected AND extracted fields have finished loading — while loading,
  // extractedFields is empty which would incorrectly treat all saved mappings as custom.
  const customMappings = useMemo(() => {
    if (!selectedTemplate || fieldsLoading) return []
    const extractedKeys = new Set(extractedFields.map((f) => f.fieldName))
    return mappings.filter((m) => !extractedKeys.has(m.pdfFieldKey))
  }, [mappings, extractedFields, selectedTemplate, fieldsLoading])

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [activeFieldKey, setActiveFieldKey] = useState<string | null>(null)

  const openMapping = (pdfFieldKey: string) => {
    setActiveFieldKey(pdfFieldKey)
    setIsDrawerOpen(true)
  }

  const openCustomMapping = () => {
    setActiveFieldKey('')
    setIsDrawerOpen(true)
  }

  const { deletePdfField } = useBuilderDeletes()
  const [removing, setRemoving] = useState<AdminPdfField | null>(null)
  const [deleting, setDeleting] = useState(false)

  const confirmRemove = async () => {
    if (!removing) return
    setDeleting(true)
    try {
      await deletePdfField(formReferenceID, versionReferenceID, removing.referenceID)
      setRemoving(null)
      refreshMappings()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove this mapping.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <MappingTabLayout>
      <SideCardHelp>
        Pick an uploaded PDF, then map each of its fields to a form field or a default/merge-token value. Fields left
        unmapped stay blank when the PDF is generated. Use &quot;Add Custom Map&quot; to create indexed or non-extracted
        field mappings (e.g. <code>Tenant{'{{idx}}'}_FullName</code>).
      </SideCardHelp>
      <MappingTemplateRow>
        <div style={{ width: '26rem' }}>
          <Select
            label="PDF Template"
            isClearable
            optionLabelKey="name"
            optionValueKey="referenceID"
            options={templates}
            value={selectedTemplate}
            getOptionLabel={(option: AdminPdfTemplate) => option.name || 'Untitled PDF'}
            getOptionValue={(option: AdminPdfTemplate) => option.referenceID}
            onChange={(option: AdminPdfTemplate | null) => {
              setSelectedTemplate(option)
              setIsDrawerOpen(false)
              setActiveFieldKey(null)
              setRemoving(null)
            }}
          />
        </div>
        {!isReadOnly && (
          <Button
            variant="primary"
            size="small"
            iconLeft={<AddIcon />}
            type="button"
            disabled={!selectedTemplate}
            onClick={openCustomMapping}
          >
            Add Custom Map
          </Button>
        )}
      </MappingTemplateRow>

      {!selectedTemplate ? (
        <EmptyState>Choose a PDF template above to see and map its fields.</EmptyState>
      ) : (
        <MappingFieldsPanel>
          {fieldsLoading ? (
            <MappingFieldRow>Loading extracted fields…</MappingFieldRow>
          ) : extractedFields.length === 0 ? (
            <MappingFieldRow>No AcroForm fields found in this PDF.</MappingFieldRow>
          ) : (
            extractedFields.map((field) => {
              const mapping = mappingByKey.get(field.fieldName)
              const summaryTitle = describeMapping(mapping, formFieldLabelByRef)
              return (
                <MappingFieldRow key={field.fieldName}>
                  <MappingFieldKey title={field.fieldName}>{field.fieldName}</MappingFieldKey>
                  <MappingSummary title={summaryTitle}>{renderMapping(mapping, formFieldLabelByRef)}</MappingSummary>
                  {!isReadOnly && (
                    <MappingActions>
                      <Button
                        variant="tertiary"
                        useLinkStyle
                        size="small"
                        type="button"
                        onClick={() => openMapping(field.fieldName)}
                      >
                        {mapping ? 'Edit' : 'Map'}
                      </Button>
                      {mapping && (
                        <Button
                          variant="tertiary"
                          useLinkStyle
                          isDestructive
                          size="small"
                          type="button"
                          onClick={() => setRemoving(mapping)}
                        >
                          Delete
                        </Button>
                      )}
                    </MappingActions>
                  )}
                </MappingFieldRow>
              )
            })
          )}
        </MappingFieldsPanel>
      )}

      {customMappings.length > 0 && (
        <div>
          <MappingSectionHeading>Custom Field Mappings</MappingSectionHeading>
          <MappingFieldsPanel>
            {customMappings.map((mapping) => {
              const summaryTitle = describeMapping(mapping, formFieldLabelByRef)
              return (
                <MappingFieldRow key={mapping.pdfFieldKey}>
                  <MappingFieldKey title={mapping.pdfFieldKey}>{mapping.pdfFieldKey}</MappingFieldKey>
                  <MappingSummary title={summaryTitle}>
                    <MappingCustomLabel>Custom mapping</MappingCustomLabel>
                    {(mapping.formFieldReferenceID || mapping.defaultData || mapping.conditionalValue) && (
                      <span>·</span>
                    )}
                    {renderMapping(mapping, formFieldLabelByRef)}
                  </MappingSummary>
                  {!isReadOnly && (
                    <MappingActions>
                      <Button
                        variant="tertiary"
                        useLinkStyle
                        size="small"
                        type="button"
                        onClick={() => openMapping(mapping.pdfFieldKey)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="tertiary"
                        useLinkStyle
                        isDestructive
                        size="small"
                        type="button"
                        onClick={() => setRemoving(mapping)}
                      >
                        Delete
                      </Button>
                    </MappingActions>
                  )}
                </MappingFieldRow>
              )
            })}
          </MappingFieldsPanel>
        </div>
      )}

      <PdfFieldMappingDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        formReferenceID={formReferenceID}
        versionReferenceID={versionReferenceID}
        pdfFieldKey={activeFieldKey ?? ''}
        mapping={activeFieldKey ? (mappingByKey.get(activeFieldKey) ?? null) : null}
        formFieldOptions={formFieldOptions}
        onSaved={refreshMappings}
      />
      <ConfirmDeleteDialog
        isOpen={Boolean(removing)}
        title="Delete PDF field mapping"
        message={`Delete the mapping for "${removing?.pdfFieldKey}"? This can't be undone.`}
        busy={deleting}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </MappingTabLayout>
  )
}
